import camelCase from 'lodash/camelCase';
import isEmpty from 'lodash/isEmpty';
import React from 'react';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { Appearance } from '@atlaskit/button';
import { OnOpenChangeArgs } from '@atlaskit/dropdown-menu/dist/types/types';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { moreActionsDropdown } from '@atlassian/temp-observability';
import { useApplicationContext } from '../../common-context';
import { AnalyticsUiAction, AnalyticsUiSubject, analytics } from '@atlassian/temp-analytics';

// For the More Action Item to have either href of action
interface MoreActionWithHref {
  href: string;
}

interface MoreActionWithAction {
  action: (...args: unknown[]) => void;
}

export type MoreActionsItems = (MoreActionWithAction | MoreActionWithHref) & {
  id: string;
  content: React.ReactNode;
  isDisabled?: boolean;
  predicate?: boolean;
  action?: (...args: unknown[]) => void;
  href?: string;
};

interface Props {
  actions: MoreActionsItems[];
  testId?: string;
  trigger?: string;
  ariaLabel?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  appearance?: Appearance;
}

const isAuthorized = (action: MoreActionsItems) => action.predicate === undefined || action.predicate === true;

export const MoreActionsV2 = ({
  actions,
  testId,
  trigger,
  ariaLabel,
  isDisabled = false,
  isLoading = false,
  appearance = 'primary',
}: Props) => {
  const visibleActions = actions.filter(isAuthorized);
  const context = useApplicationContext();
  const name = window.location.pathname.split('/')[1];
  const pageName = name ? name : 'home-page';
  if (isEmpty(visibleActions)) {
    return <></>;
  }

  return (
    <DropdownMenu
      testId={testId ?? 'more-action-menu'}
      position="bottom right"
      triggerType="button"
      trigger={trigger ?? 'Actions'}
      onOpenChange={(attrs: OnOpenChangeArgs) => {
        if (attrs.isOpen) {
          const parentPage = context.product?.toLowerCase();
          const buttonName = pageName + '-' + trigger;
          moreActionsDropdown(parentPage, buttonName);
          /**
           * web client analytics
           */
          analytics.sendUIEvent({
            action: AnalyticsUiAction.CLICKED,
            actionSubject: AnalyticsUiSubject.DROPDOWN,
            actionSubjectId: camelCase(trigger) || 'Actions',
          });
        }
      }}
      triggerButtonProps={{
        isLoading: isLoading,
        isDisabled: isDisabled,
        appearance: appearance,
        className: 'trigger-button',
        iconAfter: <ChevronDownIcon label="More" />,
        style: {
          marginLeft: '4px',
        },
        'aria-label': ariaLabel ?? 'Manage subscription',
      }}
    >
      <DropdownItemGroup>
        {visibleActions.map(({ href, action, id, content, isDisabled }) => {
          return (
            <DropdownItem
              key={id}
              testId={`more-action-${id}`}
              {...(href ? { href } : undefined)}
              {...(action
                ? {
                    onClick: action,
                  }
                : undefined)}
              isDisabled={isDisabled ?? false}
            >
              {content}
            </DropdownItem>
          );
        })}
      </DropdownItemGroup>
    </DropdownMenu>
  );
};
